import { useState, useEffect } from "react";
import { api, apiErr, API } from "@/lib/api";
import { useFetch, Field } from "@/lib/ui";
import { useLang } from "@/context/LangContext";
import { Empty, PageHeader } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Modal } from "@/lib/ui";
import { toast } from "sonner";
import { GRADES } from "@/lib/grades";
import { Plus, Trash2, Megaphone, Pin, PinOff, Paperclip, Download } from "lucide-react";

export function pickLang(b, lang) {
  const fr = lang === "fr";
  return {
    title: (fr ? b.title_fr : b.title_en) || b.title_fr || b.title_en || "",
    body: (fr ? b.body_fr : b.body_en) || b.body_fr || b.body_en || "",
  };
}

function fmt(iso) { try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); } catch { return (iso || "").slice(0, 10); } }

// Compact read-only feed for portal homepages
export function BulletinFeed({ limit = 4 }) {
  const { L, lang } = useLang();
  const { data: items, loading } = useFetch(() => api.get("/bulletins"));
  const { data: state } = useFetch(() => api.get("/bulletins/state"));
  const lastSeen = state?.last_seen || "";
  const isNew = (b) => lastSeen ? (b.created_at || "") > lastSeen : true;
  const unreadCount = (items || []).filter(isNew).length;
  useEffect(() => {
    if (items && state !== undefined) { api.post("/bulletins/seen", {}).catch(() => {}); }
  }, [items, state]);
  if (loading || !items || items.length === 0) return null;
  return (
    <div className="bg-card border border-border rounded-lg p-5" data-testid="bulletin-feed">
      <p className="font-heading font-bold text-lg mb-3 flex items-center gap-2">
        <Megaphone className="h-5 w-5 text-primary" />{L("Bulletin Board", "Tableau d'affichage")}
        {unreadCount > 0 && <span className="ml-1 inline-flex items-center gap-1 text-xs bg-red-600 text-white rounded-full px-2 py-0.5" data-testid="bulletin-unread-dot"><span className="h-1.5 w-1.5 rounded-full bg-white" />{unreadCount} {L("new", "nouveau")}</span>}
      </p>
      <div className="space-y-3">
        {items.slice(0, limit).map((b) => {
          const c = pickLang(b, lang);
          return (
            <div key={b.id} className={`pl-3 border-l-2 ${b.pinned ? "border-red-500 bg-red-50 rounded-r py-1.5 pr-2" : "border-primary"}`} data-testid={`bulletin-item-${b.id}`}>
              <p className={`font-medium flex items-center gap-1.5 ${b.pinned ? "text-red-700" : ""}`}>
                {b.pinned && <Pin className="h-3.5 w-3.5" />}{c.title}
                {isNew(b) && <span className="text-[10px] bg-red-100 text-red-700 rounded px-1.5 py-0.5" data-testid={`bulletin-new-${b.id}`}>{L("NEW", "NOUVEAU")}</span>}
              </p>
              {c.body && <p className="text-sm text-muted-foreground whitespace-pre-line">{c.body}</p>}
              {b.attachment && <a href={`${API}/files/${b.attachment.path}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary underline mt-1" data-testid={`bulletin-att-${b.id}`}><Paperclip className="h-3 w-3" />{b.attachment.filename}</a>}
              <p className="text-[11px] text-muted-foreground mt-0.5">{fmt(b.created_at)}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Admin management view
export default function BulletinBoard() {
  const { L, lang } = useLang();
  const { data: items, loading, reload } = useFetch(() => api.get("/bulletins"));
  const [open, setOpen] = useState(false);
  const EMPTY = { title_en: "", title_fr: "", body_en: "", body_fr: "", pinned: false, send_email: false, expires_at: "", audience: "all", grade_level: "" };
  const [f, setF] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const post = async () => {
    if (!f.title_en.trim() && !f.title_fr.trim()) { toast.error(L("Enter a title (EN or FR)", "Saisissez un titre (EN ou FR)")); return; }
    if (f.audience === "grade" && !f.grade_level) { toast.error(L("Choose a grade", "Choisissez un niveau")); return; }
    try {
      const r = await api.post("/bulletins", f);
      if (file) { const fd = new FormData(); fd.append("file", file); await api.raw.post(`/bulletins/${r.id}/attachment`, fd); }
      toast.success(r.emailed ? L(`Posted · emailed ${r.emailed} parents`, `Publié · ${r.emailed} parents notifiés`) : L("Posted", "Publié"));
      setOpen(false); setF(EMPTY); setFile(null); reload();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const togglePin = async (b) => { try { await api.patch(`/bulletins/${b.id}/pin`, {}); reload(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  const del = async (b) => { if (!window.confirm(L(`Are you sure you want to delete ${pickLang(b, lang).title}?`, `Êtes-vous sûr de vouloir supprimer ${pickLang(b, lang).title} ?`))) return; try { await api.del(`/bulletins/${b.id}`); reload(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  if (loading) return <Empty title={L("Loading…", "Chargement…")} />;
  return (
    <div className="animate-fade-up">
      <PageHeader title={L("Bulletin Board", "Tableau d'affichage")}
        description={L("Post announcements shown on parent & student homepages, in English and French.", "Publiez des annonces affichées sur les pages d'accueil des parents et élèves, en anglais et en français.")}
        action={<Button onClick={() => { setF(EMPTY); setOpen(true); }} data-testid="add-bulletin-btn"><Plus className="h-4 w-4 mr-1" />{L("New announcement", "Nouvelle annonce")}</Button>} />
      {(items || []).length === 0 ? <Empty title={L("No announcements yet", "Aucune annonce")} /> : (
        <div className="space-y-3">
          {items.map((b) => {
            const c = pickLang(b, lang);
            return (
              <div key={b.id} className={`border rounded-lg p-4 flex justify-between gap-3 ${b.pinned ? "border-red-300 bg-red-50" : "bg-card border-border"}`} data-testid={`bulletin-${b.id}`}>
                <div>
                  <p className={`font-heading font-bold flex items-center gap-1.5 ${b.pinned ? "text-red-700" : ""}`}>{b.pinned && <Pin className="h-4 w-4" />}{c.title}
                    {b.expired && <span className="text-[10px] bg-secondary text-muted-foreground rounded px-1.5 py-0.5" data-testid={`bulletin-expired-${b.id}`}>{L("Expired", "Expiré")}</span>}
                  </p>
                  {c.body && <p className="text-sm text-muted-foreground mt-1 whitespace-pre-line">{c.body}</p>}
                  {b.attachment && <a href={`${API}/files/${b.attachment.path}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary underline mt-1" data-testid={`bulletin-att-${b.id}`}><Download className="h-3 w-3" />{b.attachment.filename}</a>}
                  <p className="text-xs text-muted-foreground mt-1">{fmt(b.created_at)}{b.created_by_name ? ` · ${b.created_by_name}` : ""}{b.expires_at ? ` · ${L("hides", "masqué")} ${fmt(b.expires_at)}` : ""} · {b.audience === "grade" ? b.grade_level : b.audience === "parents" ? L("Parents", "Parents") : b.audience === "teachers" ? L("Teachers", "Enseignants") : L("Everyone", "Tous")}</p>
                </div>
                <div className="flex items-start gap-1 shrink-0">
                  <Button size="icon" variant="ghost" onClick={() => togglePin(b)} title={b.pinned ? L("Unpin", "Détacher") : L("Pin to top", "Épingler")} data-testid={`bulletin-pin-${b.id}`}>
                    {b.pinned ? <PinOff className="h-4 w-4 text-red-600" /> : <Pin className="h-4 w-4" />}
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => del(b)} data-testid={`bulletin-del-${b.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <Modal open={open} onClose={() => setOpen(false)} title={L("New announcement", "Nouvelle annonce")} wide
        footer={<Button onClick={post} data-testid="post-bulletin-btn">{L("Post", "Publier")}</Button>}>
        <div className="grid grid-cols-2 gap-3">
          <Field label={L("Title (English)", "Titre (Anglais)")}><Input value={f.title_en} onChange={(e) => setF({ ...f, title_en: e.target.value })} data-testid="bulletin-title-en" /></Field>
          <Field label={L("Title (French)", "Titre (Français)")}><Input value={f.title_fr} onChange={(e) => setF({ ...f, title_fr: e.target.value })} data-testid="bulletin-title-fr" /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label={L("Description (English)", "Description (Anglais)")}><Textarea value={f.body_en} onChange={(e) => setF({ ...f, body_en: e.target.value })} data-testid="bulletin-body-en" /></Field>
          <Field label={L("Description (French)", "Description (Français)")}><Textarea value={f.body_fr} onChange={(e) => setF({ ...f, body_fr: e.target.value })} data-testid="bulletin-body-fr" /></Field>
        </div>
        <Field label={L("Auto-hide after (optional)", "Masquer automatiquement après (optionnel)")}><Input type="date" value={f.expires_at} onChange={(e) => setF({ ...f, expires_at: e.target.value })} data-testid="bulletin-expires-input" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={L("Audience", "Public")}>
            <Select value={f.audience} onValueChange={(v) => setF({ ...f, audience: v })}>
              <SelectTrigger data-testid="bulletin-audience-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{L("Everyone", "Tout le monde")}</SelectItem>
                <SelectItem value="parents">{L("Parents only", "Parents seulement")}</SelectItem>
                <SelectItem value="grade">{L("A specific grade", "Un niveau précis")}</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          {f.audience === "grade" && (
            <Field label={L("Grade", "Niveau")}>
              <Select value={f.grade_level} onValueChange={(v) => setF({ ...f, grade_level: v })}>
                <SelectTrigger data-testid="bulletin-grade-select"><SelectValue placeholder={L("Select", "Choisir")} /></SelectTrigger>
                <SelectContent>{GRADES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          )}
        </div>
        <Field label={L("Attach a file (PDF/flyer, optional)", "Joindre un fichier (PDF/flyer, optionnel)")}><Input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="bulletin-file-input" /></Field>
        <p className="text-xs text-muted-foreground">{L("Fill both languages so everyone sees it in their language. If one is empty, the other is shown.", "Remplissez les deux langues pour que chacun la voie dans sa langue. Si l'une est vide, l'autre s'affiche.")}</p>
        <div className="space-y-2 mt-2">
          <label className="flex items-center gap-2 text-sm" data-testid="bulletin-pin-label"><input type="checkbox" checked={f.pinned} onChange={(e) => setF({ ...f, pinned: e.target.checked })} data-testid="bulletin-pin-check" />{L("Pin to top & highlight in red (urgent)", "Épingler en haut & surligner en rouge (urgent)")}</label>
          <label className="flex items-center gap-2 text-sm" data-testid="bulletin-email-label"><input type="checkbox" checked={f.send_email} onChange={(e) => setF({ ...f, send_email: e.target.checked })} data-testid="bulletin-email-check" />{L("Also email this to all parents", "Envoyer aussi par email à tous les parents")}</label>
        </div>
      </Modal>
    </div>
  );
}
